/**
 * FilterDrawer component
 *
 * Top drawer for property filters (price, bedrooms, baths, property type, etc.)
 * Handle has an on/off toggle that works whether drawer is open or closed.
 */
import { injectStyles } from './styles.js';
// Price options for datalists
const MIN_PRICE_OPTIONS = [
    { value: 500000, label: '$500K' },
    { value: 750000, label: '$750K' },
    { value: 1000000, label: '$1M' },
    { value: 1500000, label: '$1.5M' },
    { value: 2000000, label: '$2M' },
    { value: 3000000, label: '$3M' },
    { value: 5000000, label: '$5M' },
];
const MAX_PRICE_OPTIONS = [
    { value: 1000000, label: '$1M' },
    { value: 2000000, label: '$2M' },
    { value: 3000000, label: '$3M' },
    { value: 4000000, label: '$4M' },
    { value: 6000000, label: '$6M' },
    { value: 8000000, label: '$8M' },
    { value: 10000000, label: '$10M' },
];
const ALL_TYPES = ['house', 'condo', 'land'];
const STR_DEFAULT = 'all';
const SQFT_OPTIONS = [500, 1000, 1500, 2000, 3000, 5000];
// Min-only dropdowns (same "N+" idiom as Beds/Baths) - a max-only "just
// listed" case is redundant with the New attribute, and a full min/max
// pair isn't the realistic buyer use case for either field.
const YEAR_BUILT_OPTIONS = [2020, 2010, 2000, 1990];
const DAYS_ON_MARKET_OPTIONS = [30, 60, 90, 180];
export class FilterDrawer {
    container;
    filters;
    // Derived from `filters` (see isFiltersActive()) - not independently
    // settable by the user. setFiltersOn() remains a raw override escape
    // hatch used only by Ask mode to force the visual OFF state.
    filtersOn;
    lastEmittedActive;
    onFilterChange;
    onToggle;
    isOpen = false;
    // The Ask toggle only appears once a fresh Ask result exists (setAskActive);
    // askVisible and askOn are always equal - there's nothing to switch back on
    // once it's been turned off, since that clears the underlying Ask result.
    askVisible;
    askOn;
    onAskToggle;
    keywordDebounceTimer = null;
    constructor(options) {
        injectStyles();
        this.container = options.container;
        this.onFilterChange = options.onFilterChange;
        this.onToggle = options.onToggle;
        this.onAskToggle = options.onAskToggle;
        this.askVisible = options.askActive ?? false;
        this.askOn = options.askActive ?? false;
        // Default: TYPE = All, STR = All - both default-on.
        this.filters = {
            minPrice: options.initialFilters?.minPrice,
            maxPrice: options.initialFilters?.maxPrice,
            minBedrooms: options.initialFilters?.minBedrooms,
            minBathrooms: options.initialFilters?.minBathrooms,
            propertyTypes: options.initialFilters?.propertyTypes || [...ALL_TYPES],
            strAllowed: options.initialFilters?.strAllowed || STR_DEFAULT,
            minYearBuilt: options.initialFilters?.minYearBuilt,
            minSqft: options.initialFilters?.minSqft,
            maxSqft: options.initialFilters?.maxSqft,
            minDaysOnMarket: options.initialFilters?.minDaysOnMarket,
            keyword: options.initialFilters?.keyword,
        };
        this.filtersOn = this.isFiltersActive();
        this.lastEmittedActive = this.filtersOn;
        this.render();
        this.attachEventListeners();
    }
    render() {
        this.container.innerHTML = `
      <div class="filter-wrapper" id="filter-drawer-component">
        <div class="filter-handle" id="filter-drawer-handle" aria-expanded="${this.isOpen ? 'true' : 'false'}">
          <svg class="filter-handle-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          <span class="filter-handle-label">Filters</span>
          <label class="filter-handle-toggle" id="filter-toggle-label">
            <input type="checkbox" id="filter-toggle" ${this.filtersOn ? 'checked' : ''} ${this.filtersOn ? '' : 'disabled'}>
            <span class="filter-handle-slider"></span>
          </label>
          ${this.askVisible ? `
          <div class="filter-handle-divider"></div>
          <span class="filter-handle-label filter-handle-label-ask">Ask</span>
          <label class="filter-handle-toggle filter-handle-toggle-ask" id="filter-ask-toggle-label">
            <input type="checkbox" id="filter-ask-toggle" ${this.askOn ? 'checked' : ''}>
            <span class="filter-handle-slider"></span>
          </label>` : ''}
        </div>
        <div class="filter-panel" id="filter-drawer-panel">
          <div class="filter-panel-body">
            <!-- Price Row -->
            <div class="filter-row">
              <div class="filter-section filter-section-price">
                <div class="filter-section-label">Min Price</div>
                <input type="text"
                  class="filter-input"
                  id="filter-min-price"
                  list="min-price-options"
                  placeholder="Any"
                  value="${this.formatPriceValue(this.filters.minPrice)}"
                >
                <datalist id="min-price-options">
                  ${MIN_PRICE_OPTIONS.map(o => `<option value="${o.label}">`).join('')}
                </datalist>
              </div>
              <div class="filter-section filter-section-price">
                <div class="filter-section-label">Max Price</div>
                <input type="text"
                  class="filter-input"
                  id="filter-max-price"
                  list="max-price-options"
                  placeholder="Any"
                  value="${this.formatPriceValue(this.filters.maxPrice)}"
                >
                <datalist id="max-price-options">
                  ${MAX_PRICE_OPTIONS.map(o => `<option value="${o.label}">`).join('')}
                </datalist>
              </div>
            </div>

            <!-- Beds/Baths Row -->
            <div class="filter-row">
              <div class="filter-section">
                <div class="filter-section-label">Beds</div>
                <select class="filter-select" id="filter-bedrooms">
                  <option value="">Any</option>
                  <option value="1" ${this.filters.minBedrooms === 1 ? 'selected' : ''}>1+</option>
                  <option value="2" ${this.filters.minBedrooms === 2 ? 'selected' : ''}>2+</option>
                  <option value="3" ${this.filters.minBedrooms === 3 ? 'selected' : ''}>3+</option>
                  <option value="4" ${this.filters.minBedrooms === 4 ? 'selected' : ''}>4+</option>
                </select>
              </div>
              <div class="filter-section">
                <div class="filter-section-label">Baths</div>
                <select class="filter-select" id="filter-bathrooms">
                  <option value="">Any</option>
                  <option value="1" ${this.filters.minBathrooms === 1 ? 'selected' : ''}>1+</option>
                  <option value="2" ${this.filters.minBathrooms === 2 ? 'selected' : ''}>2+</option>
                  <option value="3" ${this.filters.minBathrooms === 3 ? 'selected' : ''}>3+</option>
                </select>
              </div>
            </div>

            <!-- Square Feet Row -->
            <div class="filter-row">
              <div class="filter-section filter-section-price">
                <div class="filter-section-label">Min Sqft</div>
                <input type="text"
                  class="filter-input"
                  id="filter-min-sqft"
                  list="min-sqft-options"
                  placeholder="Any"
                  value="${this.filters.minSqft ?? ''}"
                >
                <datalist id="min-sqft-options">
                  ${SQFT_OPTIONS.map(v => `<option value="${v}">`).join('')}
                </datalist>
              </div>
              <div class="filter-section filter-section-price">
                <div class="filter-section-label">Max Sqft</div>
                <input type="text"
                  class="filter-input"
                  id="filter-max-sqft"
                  list="max-sqft-options"
                  placeholder="Any"
                  value="${this.filters.maxSqft ?? ''}"
                >
                <datalist id="max-sqft-options">
                  ${SQFT_OPTIONS.map(v => `<option value="${v}">`).join('')}
                </datalist>
              </div>
            </div>

            <!-- Year Built / Days on Market Row -->
            <div class="filter-row">
              <div class="filter-section">
                <div class="filter-section-label">Year Built</div>
                <select class="filter-select" id="filter-year-built">
                  <option value="">Any</option>
                  ${YEAR_BUILT_OPTIONS.map(y => `<option value="${y}" ${this.filters.minYearBuilt === y ? 'selected' : ''}>${y}+</option>`).join('')}
                </select>
              </div>
              <div class="filter-section">
                <div class="filter-section-label">Days on Market</div>
                <select class="filter-select" id="filter-days-on-market">
                  <option value="">Any</option>
                  ${DAYS_ON_MARKET_OPTIONS.map(d => `<option value="${d}" ${this.filters.minDaysOnMarket === d ? 'selected' : ''}>${d}+</option>`).join('')}
                </select>
              </div>
            </div>

            <!-- Type Row (Multi-select) -->
            <div class="filter-row">
              <div class="filter-section filter-section-wide">
                <div class="filter-section-label">Type</div>
                <div class="filter-chips" id="filter-type-chips">
                  ${this.renderTypeChips()}
                </div>
              </div>
            </div>

            <!-- STR Row (Multi-select) -->
            <div class="filter-row">
              <div class="filter-section filter-section-wide">
                <div class="filter-section-label">Allows STR</div>
                <div class="filter-chips" id="filter-str-chips">
                  ${this.renderStrChips()}
                </div>
              </div>
            </div>

            <!-- Keyword Row -->
            <div class="filter-row">
              <div class="filter-section filter-section-wide">
                <div class="filter-section-label">Keyword</div>
                <input type="text"
                  class="filter-input"
                  id="filter-keyword"
                  placeholder="e.g. pool, ocean view, renovated"
                  value="${this.escapeAttr(this.filters.keyword ?? '')}"
                >
              </div>
            </div>

            <!-- Actions -->
            <div class="filter-actions">
              <button class="filter-clear-btn" id="filter-clear">Clear</button>
            </div>
          </div>
        </div>
      </div>
    `;
    }
    renderTypeChips() {
        // All and the individual chips are visually mutually exclusive - when
        // All is active the underlying array still literally equals all three
        // types (unchanged data model), but showing every chip lit up at once
        // reads as noisier/less clear than a single "All" indicator.
        const isAll = (this.filters.propertyTypes?.length ?? 0) === ALL_TYPES.length;
        const types = [
            { value: 'all', label: 'All' },
            { value: 'house', label: 'House' },
            { value: 'condo', label: 'Condo' },
            { value: 'land', label: 'Land' },
        ];
        return types.map(t => {
            const active = t.value === 'all' ? isAll : (!isAll && !!this.filters.propertyTypes?.includes(t.value));
            return `
      <label class="filter-chip ${active ? 'active' : ''}">
        <input type="checkbox" value="${t.value}" ${active ? 'checked' : ''}>
        <span>${t.label}</span>
      </label>
    `;
        }).join('');
    }
    // Genuinely mutually exclusive (unlike TYPE's multi-select), so these are
    // real radio inputs, not checkboxes - the browser's own grouping handles
    // "picking one deselects the others" natively, no JS bookkeeping needed.
    renderStrChips() {
        const current = this.filters.strAllowed ?? STR_DEFAULT;
        const options = [
            { value: 'all', label: 'All' },
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
        ];
        return options.map(o => {
            const active = current === o.value;
            return `
      <label class="filter-chip ${active ? 'active' : ''}">
        <input type="radio" name="filter-str" value="${o.value}" ${active ? 'checked' : ''}>
        <span>${o.label}</span>
      </label>
    `;
        }).join('');
    }
    escapeAttr(s) {
        return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    parseSqftValue(value) {
        if (!value)
            return undefined;
        const num = parseInt(value.replace(/[,\s]/g, ''), 10);
        return isNaN(num) ? undefined : num;
    }
    formatPriceValue(price) {
        if (price === undefined)
            return '';
        if (price >= 1000000) {
            return `$${(price / 1000000)}M`.replace('.0M', 'M');
        }
        return `$${price / 1000}K`.replace('.0K', 'K');
    }
    parsePriceValue(value) {
        if (!value)
            return undefined;
        // Remove $ and any spaces
        const cleaned = value.replace(/[$,\s]/g, '').toUpperCase();
        if (cleaned.endsWith('M')) {
            return parseFloat(cleaned.replace('M', '')) * 1000000;
        }
        if (cleaned.endsWith('K')) {
            return parseFloat(cleaned.replace('K', '')) * 1000;
        }
        const num = parseFloat(cleaned);
        return isNaN(num) ? undefined : num;
    }
    attachEventListeners() {
        const handle = this.container.querySelector('#filter-drawer-handle');
        const toggleLabel = this.container.querySelector('#filter-toggle-label');
        const toggle = this.container.querySelector('#filter-toggle');
        const clearBtn = this.container.querySelector('#filter-clear');
        // Toggle click - doesn't open drawer
        toggleLabel?.addEventListener('click', (e) => {
            e.stopPropagation();
        });
        // Disabled whenever inactive (see render()), so the only reachable
        // interaction is unchecking it while active - always means "clear".
        toggle?.addEventListener('change', () => {
            this.clearFilters();
        });
        const askToggleLabel = this.container.querySelector('#filter-ask-toggle-label');
        const askToggle = this.container.querySelector('#filter-ask-toggle');
        askToggleLabel?.addEventListener('click', (e) => {
            e.stopPropagation();
        });
        askToggle?.addEventListener('change', () => {
            this.askOn = askToggle.checked;
            if (this.onAskToggle) {
                this.onAskToggle(this.askOn);
            }
        });
        // Handle click toggles drawer (but not on either toggle)
        handle?.addEventListener('click', (e) => {
            const target = e.target;
            // Don't toggle drawer if clicking on a toggle switch
            if (target.closest('#filter-toggle-label') || target.closest('#filter-ask-toggle-label')) {
                return;
            }
            e.stopPropagation();
            if (this.isOpen) {
                this.close();
            }
            else {
                this.open();
            }
        });
        clearBtn?.addEventListener('click', () => {
            this.clearFilters();
        });
        // Price inputs
        const minPriceInput = this.container.querySelector('#filter-min-price');
        const maxPriceInput = this.container.querySelector('#filter-max-price');
        minPriceInput?.addEventListener('change', () => {
            this.filters.minPrice = this.parsePriceValue(minPriceInput.value);
            this.emitChange();
        });
        maxPriceInput?.addEventListener('change', () => {
            this.filters.maxPrice = this.parsePriceValue(maxPriceInput.value);
            this.emitChange();
        });
        // Beds/Baths selects
        const bedsSelect = this.container.querySelector('#filter-bedrooms');
        const bathsSelect = this.container.querySelector('#filter-bathrooms');
        bedsSelect?.addEventListener('change', () => {
            this.filters.minBedrooms = bedsSelect.value ? parseInt(bedsSelect.value, 10) : undefined;
            this.emitChange();
        });
        bathsSelect?.addEventListener('change', () => {
            this.filters.minBathrooms = bathsSelect.value ? parseInt(bathsSelect.value, 10) : undefined;
            this.emitChange();
        });
        // Square Feet inputs
        const minSqftInput = this.container.querySelector('#filter-min-sqft');
        const maxSqftInput = this.container.querySelector('#filter-max-sqft');
        minSqftInput?.addEventListener('change', () => {
            this.filters.minSqft = this.parseSqftValue(minSqftInput.value);
            this.emitChange();
        });
        maxSqftInput?.addEventListener('change', () => {
            this.filters.maxSqft = this.parseSqftValue(maxSqftInput.value);
            this.emitChange();
        });
        // Year Built / Days on Market selects
        const yearBuiltSelect = this.container.querySelector('#filter-year-built');
        const daysOnMarketSelect = this.container.querySelector('#filter-days-on-market');
        yearBuiltSelect?.addEventListener('change', () => {
            this.filters.minYearBuilt = yearBuiltSelect.value ? parseInt(yearBuiltSelect.value, 10) : undefined;
            this.emitChange();
        });
        daysOnMarketSelect?.addEventListener('change', () => {
            this.filters.minDaysOnMarket = daysOnMarketSelect.value ? parseInt(daysOnMarketSelect.value, 10) : undefined;
            this.emitChange();
        });
        // Keyword input - debounced on keystroke (not just blur like the
        // others) since the caller does an async server round-trip for this
        // one; waiting for blur would feel laggy for a text field.
        const keywordInput = this.container.querySelector('#filter-keyword');
        keywordInput?.addEventListener('input', () => {
            if (this.keywordDebounceTimer)
                clearTimeout(this.keywordDebounceTimer);
            this.keywordDebounceTimer = setTimeout(() => {
                this.filters.keyword = keywordInput.value.trim() || undefined;
                this.emitChange();
            }, 400);
        });
        // Type chips
        const typeChips = this.container.querySelector('#filter-type-chips');
        typeChips?.addEventListener('change', (e) => {
            const target = e.target;
            if (target.type === 'checkbox') {
                this.updateTypeSelection(target.value, target.checked);
            }
        });
        // STR chips are radios - a single value is always selected
        const strChips = this.container.querySelector('#filter-str-chips');
        strChips?.addEventListener('change', (e) => {
            const target = e.target;
            if (target.type === 'radio') {
                this.updateStrSelection(target.value);
            }
        });
        // Escape key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });
    }
    updateTypeSelection(value, checked) {
        if (value === 'all') {
            this.filters.propertyTypes = [...ALL_TYPES];
            this.renderTypeChipsInPlace();
            this.emitChange();
            return;
        }
        if (!this.filters.propertyTypes) {
            this.filters.propertyTypes = [];
        }
        // Individual chips render unchecked while All is active (the array
        // still literally contains all three types), so a click here always
        // means "just this one" - the plain add-to-array logic below would
        // otherwise silently no-op, since the clicked value is already present
        // in the underlying All-state array. Same fix as STR's break-out-of-All
        // case.
        if (this.filters.propertyTypes.length === ALL_TYPES.length) {
            this.filters.propertyTypes = [value];
            this.renderTypeChipsInPlace();
            this.emitChange();
            return;
        }
        if (checked) {
            if (!this.filters.propertyTypes.includes(value)) {
                this.filters.propertyTypes.push(value);
            }
        }
        else {
            this.filters.propertyTypes = this.filters.propertyTypes.filter(t => t !== value);
            // Don't leave an empty-but-actually-filters-nothing selection sitting
            // around looking like "nothing checked" - snap back to All instead.
            if (this.filters.propertyTypes.length === 0) {
                this.filters.propertyTypes = [...ALL_TYPES];
            }
        }
        this.renderTypeChipsInPlace();
        this.emitChange();
    }
    updateStrSelection(value) {
        this.filters.strAllowed = value;
        this.renderStrChipsInPlace();
        this.emitChange();
    }
    // Chip groups use event delegation on the container (attached once in
    // attachEventListeners()), so replacing their innerHTML here doesn't lose
    // any listeners - simpler and more correct than patching individual
    // checkboxes now that a click can change MULTIPLE chips' state at once
    // (e.g. clicking "All" checks every individual chip; unchecking the last
    // individual chip un-checks itself and re-checks "All").
    renderTypeChipsInPlace() {
        const container = this.container.querySelector('#filter-type-chips');
        if (container)
            container.innerHTML = this.renderTypeChips();
    }
    renderStrChipsInPlace() {
        const container = this.container.querySelector('#filter-str-chips');
        if (container)
            container.innerHTML = this.renderStrChips();
    }
    // Filters are "active" the moment any value deviates from all-defaults
    // (TYPE=All, STR=All, no price/beds/baths set) - the master toggle is a
    // pure reflection of this, not independently settable.
    isFiltersActive() {
        const f = this.filters;
        return (f.minPrice !== undefined ||
            f.maxPrice !== undefined ||
            f.minBedrooms !== undefined ||
            f.minBathrooms !== undefined ||
            (f.propertyTypes?.length ?? 0) !== ALL_TYPES.length ||
            (f.strAllowed ?? STR_DEFAULT) !== STR_DEFAULT ||
            f.minYearBuilt !== undefined ||
            f.minSqft !== undefined ||
            f.maxSqft !== undefined ||
            f.minDaysOnMarket !== undefined ||
            !!f.keyword);
    }
    emitChange() {
        const active = this.isFiltersActive();
        this.filtersOn = active;
        const toggle = this.container.querySelector('#filter-toggle');
        if (toggle) {
            toggle.checked = active;
            toggle.disabled = !active;
        }
        if (this.onFilterChange) {
            this.onFilterChange({ ...this.filters });
        }
        // Only fire onToggle on an actual on/off transition, not on every
        // change - avoids main.ts redundantly re-running its own filter
        // pipeline twice per change (once via onFilterChange, once via this).
        if (active !== this.lastEmittedActive) {
            this.lastEmittedActive = active;
            if (this.onToggle) {
                this.onToggle(active);
            }
        }
    }
    clearFilters() {
        this.filters = {
            propertyTypes: [...ALL_TYPES],
            strAllowed: STR_DEFAULT,
        };
        this.render();
        this.attachEventListeners();
        if (this.isOpen) {
            this.container.querySelector('#filter-drawer-component')?.classList.add('open');
        }
        this.emitChange();
    }
    open() {
        const drawer = this.container.querySelector('#filter-drawer-component');
        drawer?.classList.add('open');
        this.container.querySelector('#filter-drawer-handle')?.setAttribute('aria-expanded', 'true');
        this.isOpen = true;
    }
    close() {
        const drawer = this.container.querySelector('#filter-drawer-component');
        drawer?.classList.remove('open');
        this.container.querySelector('#filter-drawer-handle')?.setAttribute('aria-expanded', 'false');
        this.isOpen = false;
    }
    isDrawerOpen() {
        return this.isOpen;
    }
    getFilters() {
        return { ...this.filters };
    }
    getFiltersOn() {
        return this.isFiltersActive();
    }
    setFiltersOn(enabled) {
        this.filtersOn = enabled;
        const toggle = this.container.querySelector('#filter-toggle');
        if (toggle) {
            toggle.checked = enabled;
        }
    }
    setFilters(filters) {
        this.filters = { ...this.filters, ...filters };
        this.render();
        this.attachEventListeners();
        if (this.isOpen) {
            this.container.querySelector('#filter-drawer-component')?.classList.add('open');
        }
        // Previously didn't notify callers at all - e.g. MarketStats' "drill to
        // listings" flow called this expecting it to actually constrain what's
        // shown, but neither onFilterChange nor onToggle ever fired, so the
        // caller's own separate setFiltersOn(true) call was papering over a
        // filter that was never actually applied.
        this.emitChange();
    }
    getAskActive() {
        return this.askOn;
    }
    /** Shows (and turns on) or hides (and turns off) the Ask result toggle. */
    setAskActive(active) {
        this.askVisible = active;
        this.askOn = active;
        this.render();
        this.attachEventListeners();
        if (this.isOpen) {
            this.container.querySelector('#filter-drawer-component')?.classList.add('open');
        }
    }
}
//# sourceMappingURL=FilterDrawer.js.map